import React, { useEffect } from 'react';

import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Formik, Form as FormikForm } from 'formik';
import { useDispatch } from 'react-redux';
import { Alert, Button, Form } from 'react-bootstrap';

import { login } from 'store/oauth/service';
import { setAuthType } from 'store/oauth/reducer';
import {
  BillingTabs,
  GroupManagementTabs,
  LogoutRedirectionURLs,
  OrgStatusError
} from 'constants/enums';
import { getPath } from 'routes';
import { Input } from 'components/Forms';
import { LockIcon, MailIcon } from 'assets/icons';
import { AccessRoles, OAuthCode } from 'constants/enums';
import { Loading } from 'components/Fetch';
import { useAppSelector, useLoginRouter, useQuery } from 'hooks';
import { SELF_SIGNUP_ENABLED } from 'constants/selfSignup';
import { dtpSignupHref, prefilledEmail } from 'utils/loginRouter';

import { postLoginRoute } from './postLoginRoute';
import OauthWrapper from './OauthWrapper';
import TfaAuth from './TfaAuth';

const OAuth = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const navigate = useNavigate();
  const data = useAppSelector((state) => state.oauth.data);
  const loading = useAppSelector((state) => state.oauth.loading);
  const error = useAppSelector((state) => state.oauth.error);
  const loginError = useAppSelector((state) => state.oauth.loginError);
  const authType = useAppSelector((state) => state.oauth.authType);
  const logoutRedirection = useAppSelector((state) => state.oauth.logoutRedirection);
  const redirectionOffset = 5000; // arbitrary delay to prevent login redirection during logout

  const query = useQuery();
  // Empty unless this build is the front door with a DTP address configured; when set, the signup
  // CTA points at DTP instead of a local route this build does not have (§3.5).
  const signupAwayHref = dtpSignupHref(getPath(false).signup);
  // An address carried over by the identity router, so the provider does not type it twice.
  const initialValues: IOAuthForm = {
    username: prefilledEmail(query),
    password: ''
  };

  // Resolution happens on blur of the address, before there is a password to lose (design §3.2).
  const { onEmailBlur } = useLoginRouter(getPath(false).login);

  const sithsAuth = import.meta.env.VITE_SITHS_AUTHENTICATION;

  useEffect(() => {
    if (sithsAuth && !data?.access_token) {
      const clientID = import.meta.env.VITE_CLIENT_ID;
      const portalID = import.meta.env.VITE_AUTHENTICATION_PORTAL_ID;
      const authenticationUrl = import.meta.env.VITE_AUTHENTICATION_URL;
      const params = new URLSearchParams(window.location.search);
      const auth = params.get('auth');

      if (!logoutRedirection || Date.now() - logoutRedirection > redirectionOffset) {
        if (auth === 'se-idp' || (auth !== 'siths' && authType === 'se-idp')) {
          dispatch(setAuthType('se-idp'));
          const urlParameters = '?state=' + portalID;
          window.location.replace(
            import.meta.env.DEV === 'development'
              ? 'http://localhost:3001/api/se-idp' + urlParameters
              : '/api/se-idp' + urlParameters
          );
        } else {
          dispatch(setAuthType('siths'));
          const urlParameters = '?response_type=code&client_id=' + clientID + '&state=' + portalID;
          window.location.replace(
            authenticationUrl +
              '/siths_card_authentication/siths_card_authentication.php' +
              urlParameters
          );
        }
      } else {
        if (authType === 'se-idp') {
          window.location.replace(LogoutRedirectionURLs.SEIDP);
        } else if (authType === 'siths') {
          window.location.replace(LogoutRedirectionURLs.SITHS);
        }
      }
    } else {
      if (data?.access_token) {
        if (
          data.code &&
          (data.code === OAuthCode.TFA_CODE_REQUIRED ||
            data.code === OAuthCode.TFA_PHONE_MISSING ||
            data.code === OrgStatusError.SUSPENDED)
        ) {
          //special handling - TFA & Org Status
          return;
        }

        navigate(postLoginRoute(data.roles || [], data.role));
      }
    }
  }, [sithsAuth, data, navigate, authType, logoutRedirection, dispatch]);

  return (
    <>
      {loading || sithsAuth ? (
        <Loading />
      ) : (
        <OauthWrapper>
          {error && (
            <Alert variant="danger" data-test="alertCred">
              {intl.formatMessage({ id: 'wrongCredentials' })}
            </Alert>
          )}
          {loginError === 'noAccess' && (
            <Alert variant="danger" data-test="alertNoAccess">
              {intl.formatMessage({ id: 'loginNoAccessKey' })}
            </Alert>
          )}
          {data?.code &&
          OrgStatusError.SUSPENDED === data.code &&
          data?.codeMsg &&
          data.codeMsg.length > 0 ? (
            <Alert variant="danger" data-test="alertSuspendedOrg">
              {intl.formatMessage({ id: data.codeMsg })}
            </Alert>
          ) : (
            data?.code && <TfaAuth />
          )}
          <Formik
            initialValues={initialValues}
            onSubmit={(values: IOAuthForm) => {
              dispatch(login(values));
            }}
            validate={(values: IOAuthForm) => {
              const errors: any = {};
              if (!values.username) {
                errors.username = intl.formatMessage({ id: 'usernameRequiredKey' });
              }
              if (!values.password) {
                errors.password = intl.formatMessage({ id: 'passwordRequiredKey' });
              }
              return errors;
            }}
          >
            {({ isValid }) => {
              return (
                <FormikForm autoComplete="off">
                  <Form.Group className="form-group mb-4" controlId="username">
                    <Input
                      type="username"
                      name="username"
                      label={intl.formatMessage({ id: 'signupWorkEmailKey' })}
                      placeholder="you@yourpractice.com"
                      icon={<MailIcon className="icon" />}
                      onBlur={onEmailBlur}
                      data-test="username"
                    />
                  </Form.Group>
                  <Form.Group className="form-group mb-4" controlId="password">
                    <Input
                      type="password"
                      name="password"
                      label={intl.formatMessage({ id: 'Password' })}
                      labelAction={
                        <Link
                          to={getPath().forgotPassword}
                          className="login-link"
                          data-test="forgotPassword"
                        >
                          {intl.formatMessage({ id: 'forgotPasswordKey' })}
                        </Link>
                      }
                      placeholder={intl.formatMessage({ id: 'loginPasswordPlaceholderKey' })}
                      icon={<LockIcon className="icon" />}
                      reveal
                      revealLabel={intl.formatMessage({ id: 'loginTogglePasswordKey' })}
                      data-test="password"
                    />
                  </Form.Group>
                  <Button
                    className="btn-login"
                    variant="primary"
                    type="submit"
                    disabled={!isValid}
                    data-test="submit"
                  >
                    {intl.formatMessage({ id: 'loginKey' })}
                  </Button>
                </FormikForm>
              );
            }}
          </Formik>
          {(SELF_SIGNUP_ENABLED || signupAwayHref) && (
            <div data-test="signup-entry">
              <div className="login-divider my-4">
                {intl.formatMessage({ id: 'signupNewToCareSimpleKey' })}
              </div>
              {/*
               * Same CTA, same copy, two destinations. On DTP it opens the wizard here; on the front
               * door it leaves for DTP, because signup belongs to one environment (design §3.5) and
               * an account that does not exist yet has nothing to resolve. A person arriving at
               * app.caresimple.com to sign up must not have to know which environment that is.
               *
               * An anchor rather than a Link for the away case: react-router would treat an absolute
               * URL as an in-app path and render notFound, since this build has no signup route.
               */}
              {signupAwayHref ? (
                <a
                  href={signupAwayHref}
                  className="btn btn-login-secondary d-flex align-items-center justify-content-center"
                  data-test="signupLink"
                >
                  {intl.formatMessage({ id: 'signupCreatePracticeAccountKey' })}
                </a>
              ) : (
                <Link
                  to={getPath().signup}
                  className="btn btn-login-secondary d-flex align-items-center justify-content-center"
                  data-test="signupLink"
                >
                  {intl.formatMessage({ id: 'signupCreatePracticeAccountKey' })}
                </Link>
              )}
              <div className="login-subcaption text-center mt-3">
                {intl.formatMessage({ id: 'signupPrepaidTaglineKey' })}
              </div>
              <div className="login-note text-center">
                {intl.formatMessage({ id: 'signupHealthSystemNoteKey' })}
              </div>
            </div>
          )}
        </OauthWrapper>
      )}
    </>
  );
};

export default OAuth;
